import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { StoredCopyDayOperation } from '../services/copyDayOperation';
import { formatDate, todayIso } from '../utils';
import { isCanonicalDate } from '../utils/calendarDate';
import DateInput, { DateInputValidity } from './DateInput';

export const copyDayFeedback = (op: StoredCopyDayOperation, startDate: string, endDate: string) => {
  if (op.state === 'copied' && op.result) {
    const outside = op.destinationDate < startDate || op.destinationDate > endDate;
    return `Copied ${op.result.copiedCount} entries to ${formatDate(op.destinationDate)}${outside ? '; destination is outside the displayed weeks.' : '.'}`;
  }
  if (op.state === 'noRecords') return `No eligible entries remained on ${formatDate(op.sourceDate)}. No entries were copied.`;
  if (op.state === 'rejected' || op.state === 'notSubmitted') return op.message || 'The copy was rejected. Check the source entries and dates before confirming a new operation.';
  return op.message || `Copying ${formatDate(op.sourceDate)} to ${formatDate(op.destinationDate)}. Keep this operation for recovery until its outcome is known.`;
};

interface CopyDayDialogProps {
  sourceDate: string;
  operation?: StoredCopyDayOperation;
  busy: boolean;
  error: string;
  startDate: string;
  endDate: string;
  onSubmit: (destination: string) => Promise<void>;
  onRetry: () => void;
  onCheckStatus: () => void;
  onRetryRefresh: () => void;
  onClose: () => void;
  onAcknowledge: () => void;
}

const CopyDayDialog: React.FC<CopyDayDialogProps> = ({ sourceDate, operation, busy, error, startDate, endDate,
  onSubmit, onRetry, onCheckStatus, onRetryRefresh, onClose, onAcknowledge }) => {
  const [destination, setDestination] = useState(() => operation ? operation.destinationDate : todayIso());
  const [submitting, setSubmitting] = useState(false);
  const [validity, setValidity] = useState<DateInputValidity>({ rawValue: '', canonicalValue: '', state: 'empty' });
  const guard = useRef(false);
  const dialog = useRef<HTMLElement>(null);
  const dismiss = useRef<() => void>(() => {});
  const editing = !operation || operation.state === 'rejected' || operation.state === 'notSubmitted';
  const valid = validity.state === 'valid' && isCanonicalDate(destination) && destination !== sourceDate;
  const working = busy || submitting;
  const canDismiss = !working && !guard.current && (editing || operation?.state === 'noRecords' || (operation?.state === 'copied' && !operation.needsRefresh));
  dismiss.current = () => { if (canDismiss) operation ? onAcknowledge() : onClose(); };

  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    const firstInput = dialog.current?.querySelector<HTMLInputElement>('input:not(:disabled):not([tabindex="-1"])');
    (firstInput || dialog.current)?.focus();
    const keepFocus = (event: FocusEvent) => {
      if (dialog.current && !dialog.current.contains(event.target as Node)) dialog.current.focus();
    };
    document.addEventListener('focusin', keepFocus);
    return () => {
      document.removeEventListener('focusin', keepFocus);
      if (trigger?.isConnected && trigger !== document.body && !trigger.matches(':disabled') &&
        !trigger.closest('[hidden], [inert], [aria-hidden="true"]')) trigger.focus();
      else document.getElementById('dashboard-title')?.focus();
    };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing || !valid || busy || guard.current) return;
    guard.current = true;
    setSubmitting(true);
    try { await onSubmit(destination); } finally { guard.current = false; setSubmitting(false); }
  };

  const validationMessage = validity.state === 'invalid' ? 'Enter a real calendar date in dd.MM.yyyy format.'
    : validity.state === 'incomplete' ? 'Complete the destination date in dd.MM.yyyy format.'
    : destination === sourceDate ? 'Destination must differ from the source date.' : '';

  return <div className="time-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) dismiss.current(); }}>
    <section ref={dialog} className="time-modal copy-day-modal" role="dialog" aria-modal="true" aria-labelledby="copy-day-title"
      aria-describedby="copy-day-explanation" aria-busy={working} tabIndex={-1}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); dismiss.current(); return; }
        if (event.key !== 'Tab') return;
        const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled):not([tabindex="-1"])') || []);
        const first = controls[0]; const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); dialog.current?.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first.focus(); }
      }}>
      <div className="time-modal-header"><h2 id="copy-day-title" className="card-title">Copy day</h2></div>
      <div className="time-modal-body">
        <p>Source date: <strong>{formatDate(sourceDate)}</strong></p>
        <p id="copy-day-explanation">All eligible personal entries will be appended. Existing entries on the destination date will remain.</p>
        <form className="time-form" onSubmit={submit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="copy-day-destination">Destination date</label>
            <DateInput id="copy-day-destination" ariaLabel="Destination date" value={destination} onChange={setDestination} onValidityChange={setValidity}
              strict required disabled={!editing || working} aria-describedby="copy-day-validation" aria-invalid={!!validationMessage} />
            <span id="copy-day-validation" className="copy-day-validation" role="alert">{editing ? validationMessage : ''}</span>
          </div>
          {error && <p role="alert" className="alert alert-danger">{error}</p>}
          {operation && <p role={operation.state === 'rejected' || operation.state === 'notSubmitted' ? 'alert' : 'status'}>{copyDayFeedback(operation, startDate, endDate)}</p>}
          {operation?.state === 'copied' && operation.message && <p role="alert">{operation.message}</p>}
          {busy && <p role="status">{operation?.state === 'copied' ? 'Refreshing Dashboard...' : 'Checking or submitting this operation...'}</p>}
          <div className="form-actions">
            {editing && <button className="btn btn-primary" type="submit" disabled={!valid || working}>{operation ? 'Confirm new copy' : 'Copy day'}</button>}
            {operation?.state === 'uncertain' && <>
              <button className="btn btn-primary" type="button" disabled={busy} onClick={onRetry}>Retry this operation</button>
              <button className="btn btn-secondary" type="button" disabled={busy} onClick={onCheckStatus}>Check status</button>
              <button className="btn btn-secondary" type="button" disabled={busy} onClick={onClose}>Keep for recovery</button>
            </>}
            {operation?.state === 'copied' && operation.needsRefresh && <button className="btn btn-primary" type="button" disabled={busy} onClick={onRetryRefresh}>Retry Dashboard refresh</button>}
            {canDismiss && <button className="btn btn-secondary" type="button" onClick={() => dismiss.current()}>{operation ? 'Done' : 'Cancel'}</button>}
          </div>
        </form>
      </div>
    </section>
  </div>;
};

export default CopyDayDialog;
